"use client";

import Image from "next/image";
import CMSBoardingSchoolGallery from "./CMSGallery";
import { useRef, useEffect, useState } from "react";
import EnrollTourButtons from "@/components/cms/EnrollTourButtons";
import { BoardingGallery } from "@/types/contentful";
import { motion } from "framer-motion";
import { useParallaxOffset } from "@/hooks/useParallaxOffset";

interface BoardingSchoolFacilitiesProps {
  boardingImages: BoardingGallery[];
  cms?: Record<string, string>;
}

// Custom hook for viewport detection (once: never resets to false once visible)
function useInView(
  ref: { current: HTMLElement | null },
  options?: { margin?: string }
) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin: options?.margin || "0px" }
    );
    const node = ref.current;
    if (node) observer.observe(node);
    return () => {
      if (node) observer.unobserve(node);
      observer.disconnect();
    };
  }, [ref, options?.margin]);
  return inView;
}

const defaultFeatures: string[] = [];

export default function BoardingSchoolFacilities({
  boardingImages,
  cms,
}: BoardingSchoolFacilitiesProps) {
  const featuresLeft: string[] = (() => {
    try {
      const parsed = JSON.parse(cms?.features_left ?? "");
      return Array.isArray(parsed) ? parsed : defaultFeatures;
    } catch { return defaultFeatures; }
  })();
  const featuresRight: string[] = (() => {
    try {
      const parsed = JSON.parse(cms?.features_right ?? "");
      return Array.isArray(parsed) ? parsed : defaultFeatures;
    } catch { return defaultFeatures; }
  })();
  const parallaxRef = useRef<HTMLDivElement>(null);

  // Use optimized parallax hook with stronger effect (0.4)
  const [offset, isDesktop, transform3d] = useParallaxOffset(0.4, 640, parallaxRef);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Animated heading
  const headingRef = useRef<HTMLDivElement>(null);
  const showHeading = useInView(headingRef, {
    margin: "0px",
  });

  return (
    <section className="relative py-16 overflow-hidden">
      {/* Parallax Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={parallaxRef}
          className="will-change-transform absolute w-full top-0 h-full sm:-top-[45%] sm:h-[190%]"
          style={
            mounted && isDesktop ? { transform: `${transform3d} translateY(-120px)` } : {}
          }
        >
          {cms?.bg_image_url && <Image
            src={cms.bg_image_url}
            alt="Boarding School Facilities Background"
            fill
            className="object-cover object-[center_top]"
            priority
            sizes="100vw"
          />}
        </div>
        {/* Overlay — outside parallax container so it is never inside a GPU compositor layer */}
        <div className="absolute inset-0" style={{ background: cms?.overlay_color ?? "rgba(6,22,82,0.6)" }}></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-0 sm:px-6 lg:px-12">
        {/* Animated Heading */}
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 60 }}
          animate={showHeading ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
          className="mb-8 text-center"
        >
          <h2 className="text-white text-xl md:text-2xl font-light mb-2">
            {cms?.pre_heading /* || "Our Boarding School's" */}
          </h2>
          <h3 className="text-white text-4xl md:text-5xl font-bold">
            {cms?.heading /* || "FACILITIES" */}
          </h3>
        </motion.div>
        {/* Features */}
        <div className="bg-white/90 rounded-lg p-6 md:p-8 flex flex-col md:flex-row gap-8 w-full max-w-4xl mx-auto shadow-lg">
          <ul className="flex-1 space-y-3 text-left text-black text-base md:text-lg">
            {featuresLeft.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-black rounded-full mr-2"></span>
                {f}
              </li>
            ))}
          </ul>
          <ul className="flex-1 space-y-3 text-left text-black text-base md:text-lg">
            {featuresRight.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-black rounded-full mr-2"></span>
                {f}
              </li>
            ))}
          </ul>
        </div>
        {/* CTA Buttons */}
        <EnrollTourButtons
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8 mb-8"
          buttonClassName="bg-school-gold hover:bg-school-gold-dark text-black font-bold py-4 px-8 rounded text-lg transition-colors text-center"
          enrollText={cms?.enrol_btn_text}
          tourText={cms?.tour_btn_text}
          enrollUrl={cms?.enrol_btn_url /* ?? "/contact-us?option=enrol&boarding=true#contact-form" */}
          tourUrl={cms?.tour_btn_url /* ?? "/contact-us?option=tour&boarding=true#contact-form" */}
        />
        {/* Gallery Section - Inside z-10 wrapper but lightbox will use portal */}
        <CMSBoardingSchoolGallery boardingImages={boardingImages} />
      </div>
    </section>
  );
}
